import { appConfig } from '../config/appConfig';

// Gallery image interface
export interface GalleryImage {
  id: string;
  country: string;
  year: string;
  filename?: string;
  thumbnailFilename?: string;
  thumbnailUrl?: string;
  youtubeUrl?: string;
  description?: string;
  uploadedAt: any;
}

// API response interface for gallery images
interface GalleryResponse {
  items: GalleryImage[];
}

// Helper function to get admin token
const getAdminToken = (): string => {
  return btoa(`${appConfig.admin.username}:${appConfig.admin.password}`);
};

/**
 * Fetch all gallery images from the backend API
 * @param filters - Optional filters for country and year
 */
export const fetchGalleryImages = async (filters?: {
  country?: string;
  year?: string;
}): Promise<GalleryImage[]> => {
  try {
    const params = new URLSearchParams();
    if (filters?.country) params.append('country', filters.country);
    if (filters?.year) params.append('year', filters.year);
    
    const url = `/api/gallery${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data: GalleryResponse = await response.json();
    return data.items;
  } catch (error) {
    console.error('Error fetching gallery images:', error);
    throw error;
  }
};

/**
 * Fetch a single gallery image by ID
 */
export const fetchGalleryImageById = async (id: string): Promise<GalleryImage | null> => {
  try {
    const response = await fetch(`/api/gallery/${id}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching gallery image:', error);
    throw error;
  }
};

/**
 * Upload a new gallery image (admin only)
 */
export const uploadGalleryImage = async (formData: FormData): Promise<GalleryImage> => {
  try {
    const response = await fetch('/api/gallery', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getAdminToken()}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error uploading gallery image:', error);
    throw error;
  }
};

/**
 * Update gallery image metadata (admin only)
 */
export const updateGalleryImage = async (
  id: string,
  updates: Partial<Pick<GalleryImage, 'country' | 'year' | 'youtubeUrl' | 'description'>>
): Promise<GalleryImage> => {
  try {
    const response = await fetch(`/api/gallery/${id}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating gallery image:', error);
    throw error;
  }
};

/**
 * Delete a gallery image (admin only)
 */
export const deleteGalleryImage = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`/api/gallery/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getAdminToken()}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error deleting gallery image:', error);
    throw error;
  }
};

/**
 * Extract YouTube video ID from various YouTube URL formats or iframe embed code
 */
export const extractYouTubeId = (input: string): string | null => {
  if (!input) return null;

  const trimmed = input.trim();

  // If input is an iframe embed code, extract the src URL
  const iframeMatch = trimmed.match(/src=["']([^"']+)["']/);
  const urlToCheck = iframeMatch ? iframeMatch[1] : trimmed;

  // Direct 11-char video ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(urlToCheck)) {
    return urlToCheck;
  }

  // v= query param (handles watch?feature=...&v=ID and similar)
  try {
    const parsed = new URL(urlToCheck, 'https://www.youtube.com');
    const vParam = parsed.searchParams.get('v');
    if (vParam && /^[a-zA-Z0-9_-]{11}$/.test(vParam)) {
      return vParam;
    }
  } catch {
    // Fall through to regex patterns
  }

  const patterns = [
    /(?:youtube\.com\/(?:embed|shorts|live|v)\/|youtube-nocookie\.com\/embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /[?&]v=([a-zA-Z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = urlToCheck.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }

  return null;
};

/**
 * Get YouTube embed URL from video URL or iframe embed code
 * Accepts:
 * - Regular YouTube URLs (youtube.com/watch?v=...)
 * - Short URLs (youtu.be/...)
 * - Full iframe embed code
 * - Direct video IDs
 */
export const getYouTubeEmbedUrl = (input: string): string => {
  const videoId = extractYouTubeId(input);
  
  if (!videoId) {
    // If we can't extract a video ID, return empty string
    console.warn('Could not extract YouTube video ID from:', input);
    return '';
  }
  
  // Use youtube-nocookie.com for privacy-friendly embeds
  return `https://www.youtube-nocookie.com/embed/${videoId}`;
};

/**
 * Get YouTube thumbnail URL from video URL (kept for backwards compatibility)
 */
export const getYouTubeThumbnail = (url: string, quality: 'default' | 'hq' | 'maxres' = 'hq'): string | null => {
  const videoId = extractYouTubeId(url);
  if (!videoId) return null;
  
  const qualityMap = {
    default: 'default',
    hq: 'hqdefault',
    maxres: 'maxresdefault',
  };
  
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
};

/**
 * Get the full image URL for a gallery image from Cloud Storage
 */
export const getGalleryImageUrl = (filename?: string): string => {
  if (!filename) return '';
  
  // If filename already contains the full Cloud Storage URL, return it as is
  if (filename.startsWith('https://storage.googleapis.com/')) {
    return filename;
  }
  
  // Otherwise, construct the full URL
  return `https://storage.googleapis.com/raise-uploads-storage/gallery/${filename}`;
};

const GALLERY_THUMB_BASE = 'https://storage.googleapis.com/raise-uploads-storage/gallery/thumbs';

/**
 * Low-resolution WebP used by carousels and grids. Falls back to the original
 * when a thumbnail has not been generated yet.
 */
export const getGalleryThumbnailUrl = (
  image: Pick<GalleryImage, 'filename' | 'thumbnailFilename' | 'thumbnailUrl'>
): string => {
  if (image.thumbnailUrl) return image.thumbnailUrl;

  if (image.thumbnailFilename) {
    if (image.thumbnailFilename.startsWith('https://storage.googleapis.com/')) {
      return image.thumbnailFilename;
    }
    return `${GALLERY_THUMB_BASE}/${image.thumbnailFilename}`;
  }

  return getGalleryImageUrl(image.filename);
};

/**
 * One photo per country. Expects images newest-first (API order) so the
 * most recently uploaded photo represents each country.
 */
export function pickOneImagePerCountry(images: GalleryImage[]): GalleryImage[] {
  const seen = new Set<string>();
  const picked: GalleryImage[] = [];

  for (const image of images) {
    if (!image.filename?.trim()) continue;
    const country = image.country?.trim().toLowerCase();
    if (!country || seen.has(country)) continue;
    seen.add(country);
    picked.push(image);
  }

  return picked;
}

